import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type Tag = ResponseData<'/api/v1/tags/{id}', 'get'>
export type TagValue = ResponseData<'/api/v1/tags/{id}/value', 'get'>
export type TagHistory = ResponseData<'/api/v1/tags/{id}/history', 'get'>
type TagsListData = ResponseData<'/api/v1/tags/', 'get'>
type TagsQuery = QueryOf<'/api/v1/tags/', 'get'>
type HistoryQuery = QueryOf<'/api/v1/tags/{id}/history', 'get'>
type WriteValueBody = RequestBodyOf<'/api/v1/tags/{id}/value', 'post'>
type CreateTagBody = RequestBodyOf<'/api/v1/tags/', 'post'>
type UpdateTagBody = RequestBodyOf<'/api/v1/tags/{id}', 'patch'>

export const tagsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTags: builder.query<TagsListData, TagsQuery | void>({
      query: (params) => ({ url: ENDPOINTS.TAGS.ROOT, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: TagsListData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((t) => ({ type: QUERY_KEYS.TAG, id: t.id }) as const),
              { type: QUERY_KEYS.TAG, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.TAG, id: 'LIST' } as const],
    }),
    getTagById: builder.query<Tag, string>({
      query: (id) => ENDPOINTS.TAGS.BY_ID(id),
      transformResponse: (response: { data: Tag }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.TAG, id }],
    }),
    getTagValue: builder.query<TagValue, string>({
      query: (id) => ENDPOINTS.TAGS.VALUE(id),
      transformResponse: (response: { data: TagValue }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.TAG, id: `value:${id}` }],
    }),
    getTagHistory: builder.query<TagHistory, { id: string; query?: HistoryQuery }>({
      query: ({ id, query }) => ({
        url: ENDPOINTS.TAGS.HISTORY(id),
        ...(query ? { params: query } : {}),
      }),
      transformResponse: (response: { data: TagHistory }) => response.data,
      providesTags: (_r, _e, { id }) => [{ type: QUERY_KEYS.TAG_HISTORY, id }],
    }),
    writeTagValue: builder.mutation<TagValue, { id: string; body: WriteValueBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.TAGS.VALUE(id), method: 'POST', body }),
      transformResponse: (response: { data: TagValue }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: QUERY_KEYS.TAG, id: `value:${id}` }],
    }),
    createTag: builder.mutation<Tag, CreateTagBody>({
      query: (body) => ({ url: ENDPOINTS.TAGS.ROOT, method: 'POST', body }),
      transformResponse: (response: { data: Tag }) => response.data,
      invalidatesTags: [{ type: QUERY_KEYS.TAG, id: 'LIST' }],
    }),
    updateTag: builder.mutation<Tag, { id: string; body: UpdateTagBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.TAGS.BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Tag }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.TAG, id },
        { type: QUERY_KEYS.TAG, id: 'LIST' },
      ],
    }),
    deleteTag: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.TAGS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.TAG, id },
        { type: QUERY_KEYS.TAG, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetTagsQuery,
  useGetTagByIdQuery,
  useGetTagValueQuery,
  useGetTagHistoryQuery,
  useWriteTagValueMutation,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} = tagsApi
